import { useTranslation } from 'react-i18next';
import {
    BicycleIcon,
    BusIcon,
    CarIcon,
    MotorcycleIcon,
    TrashIcon,
    TruckIcon,
} from '@phosphor-icons/react';
import {
    ActionBar,
    AnimatedActionBarModeTransition,
    Combobox,
    SelectionBar,
} from '@/components';
import type { ActionConfig } from '@/components/ActionBar';
import {
    ActionType,
    DividerOrientation,
    buildFiltersAction,
    buildSortAction,
    buildMultiSelectToggle,
} from '@/components/ActionBar';
import { Permission } from '@/types/permissions';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import type { DirectorySortOption } from './types';

interface DirectoryGridActionBarProps {
    isMultiSelectMode: boolean;
    selectedCount: number;
    totalCount?: number;
    searchValue: string;
    onSearchChange: (value: string) => void;
    onSearchSubmit: (value: string) => void;
    searchPlaceholder: string;
    onEnterMultiSelect: () => void;
    onExitMultiSelect: () => void;
    onSelectAll: () => void;
    onDeleteSelected?: () => void;
    sortOptions: DirectorySortOption[];
    sortOption: string;
    onSortChange: (option: string) => void;
    vehicleTypeOptions?: string[];
    selectedVehicleTypes?: string[];
    onVehicleTypeToggle?: (type: string) => void;
    onClearVehicleTypes?: () => void;
    selectionEntityLabel: string;
}

const VEHICLE_TYPE_ICONS: Record<string, React.ElementType> = {
    car: CarIcon,
    truck: TruckIcon,
    bus: BusIcon,
    motorcycle: MotorcycleIcon,
    bicycle: BicycleIcon,
};

export function DirectoryGridActionBar({
    isMultiSelectMode,
    selectedCount,
    totalCount,
    searchValue,
    onSearchChange,
    onSearchSubmit,
    searchPlaceholder,
    onEnterMultiSelect,
    onExitMultiSelect,
    onSelectAll,
    onDeleteSelected,
    sortOptions,
    sortOption,
    onSortChange,
    vehicleTypeOptions = [],
    selectedVehicleTypes = [],
    onVehicleTypeToggle,
    onClearVehicleTypes,
    selectionEntityLabel,
}: DirectoryGridActionBarProps) {
    const { t } = useTranslation([
        I18nNamespace.DIRECTORY,
        I18nNamespace.COMMON,
    ]);

    const hasVehicleTypeFilter =
        !!onVehicleTypeToggle &&
        (vehicleTypeOptions.length > 0 || selectedVehicleTypes.length > 0);

    const filtersAction: ActionConfig | undefined = hasVehicleTypeFilter
        ? buildFiltersAction(t, {
              isActive: selectedVehicleTypes.length > 0,
              onClear: () => onClearVehicleTypes?.(),
              items: [
                  {
                      icon: CarIcon,
                      label: t('common:browseBar.vehicleType'),
                      isActive: selectedVehicleTypes.length > 0,
                      children: (
                          <Combobox
                              title={t('common:browseBar.vehicleType')}
                              options={vehicleTypeOptions.map(
                                  (vehicleType) => ({
                                      value: vehicleType,
                                      label: t(
                                          `vehicles.types.${vehicleType}`,
                                          {
                                              defaultValue:
                                                  vehicleType
                                                      .charAt(0)
                                                      .toUpperCase() +
                                                  vehicleType.slice(1),
                                          },
                                      ),
                                      icon: VEHICLE_TYPE_ICONS[vehicleType],
                                  }),
                              )}
                              selected={selectedVehicleTypes}
                              onToggle={onVehicleTypeToggle!}
                              onClear={onClearVehicleTypes}
                              clearLabel={t('common:clear')}
                              className="min-w-[200px]"
                          />
                      ),
                  },
              ],
          })
        : undefined;

    const browseActions: ActionConfig[] = [
        {
            type: ActionType.SEARCH,
            value: searchValue,
            onChange: onSearchChange,
            onSubmit: onSearchSubmit,
            placeholder: searchPlaceholder,
        },
        {
            type: ActionType.DIVIDER,
            orientation: DividerOrientation.VERTICAL,
        },
        ...(filtersAction ? [filtersAction] : []),
        buildSortAction(
            t,
            sortOptions.map((option) => ({
                label: t(`common:${option.labelKey}`),
                isActive: sortOption === option.value,
                onClick: () => onSortChange(option.value),
            })),
        ),
        ...(onDeleteSelected
            ? [buildMultiSelectToggle(t, onEnterMultiSelect)]
            : []),
    ];

    const bulkActions: ActionConfig[] = onDeleteSelected
        ? [
              {
                  type: ActionType.ITEM as const,
                  icon: TrashIcon,
                  label: t('common:actions.delete'),
                  onClick: onDeleteSelected,
                  permission: Permission.DELETE,
              },
          ]
        : [];

    return (
        <AnimatedActionBarModeTransition
            isMultiSelectMode={isMultiSelectMode}
            browseSlot={<ActionBar actions={browseActions} />}
            selectionSlot={
                <SelectionBar
                    selectedCount={selectedCount}
                    totalCount={totalCount}
                    entityLabel={selectionEntityLabel}
                    onSelectAll={onSelectAll}
                    onExitMultiSelect={onExitMultiSelect}
                    bulkActions={bulkActions}
                />
            }
        />
    );
}
